import { ArrowLeftIcon } from '@assets/icons';
import { Button, IconButton } from '@components/button';
import { Form, FormSelect, FormTextInput } from '@components/form';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import {
  InpaintInputType,
  InpaintOutputType,
  useCreateInpaintMutation,
  useGetInpaintFormatsQuery,
} from '@generated/graphql';
import { MenuItem, Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { head, sortBy } from 'lodash';

interface CreateInpaintProps {
  refetch: VoidFunction;
  folderHistory: string[];
  onClose: () => void;
  goBack: () => void;
  volumeId: string;
}

export const CreateInpaint = ({
  refetch,
  onClose,
  goBack,
  volumeId,
  folderHistory,
}: CreateInpaintProps) => {
  const { useAsyncNotification } = useNotifications();
  const { data: inpaintFormats, loading: inpaintFormatsLoading } = useGetInpaintFormatsQuery();
  const [createInpaint] = useCreateInpaintMutation();

  const sortedInpaintFormats =
    !inpaintFormatsLoading && sortBy(inpaintFormats?.getInpaintFormats, (format) => format);

  const handleFormSubmit = useAsyncNotification(
    `Successfully created the inpaint.`,
    async ({
      location,
      output,
      inputType,
      outputType,
      dilation,
    }: {
      location: string;
      output: string;
      inputType: InpaintInputType;
      outputType: InpaintOutputType;
      dilation: number;
    }) => {
      await createInpaint({
        variables: {
          volumeId,
          location,
          destination: output,
          inputType,
          outputType,
          dilation,
        },
      });
      refetch();
      onClose();
    },
  );

  return (
    <Form
      enableReinitialize
      onSubmit={handleFormSubmit}
      initialValues={{
        location: ['./', ...folderHistory].join(''),
        outputType: InpaintOutputType.PNG,
        inputType: sortedInpaintFormats && head(sortedInpaintFormats).value,
        dilation: 5,
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Stack>
          <Group alignItems="center" mb={6} gap={2}>
            <IconButton onClick={goBack} size={20} Icon={ArrowLeftIcon} />
            <Typography variant="h2">Create Inpaint</Typography>
          </Group>
          <FormTextInput label="Location" name="location" />
          <FormSelect label="Input type" name="inputType">
            {(sortedInpaintFormats || []).map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.name}
              </MenuItem>
            ))}
          </FormSelect>
          <FormTextInput
            label="Output"
            name="outputDir"
            placeholder={['./', ...folderHistory, 'cleaned/', `<timestamp>/`].join('')}
          />
          <Stack direction="row" spacing={6}>
            <FormSelect label="Output type" name="outputType">
              {[
                { name: 'PNG', value: InpaintOutputType.PNG },
                { name: 'JPG', value: InpaintOutputType.JPG },
                { name: 'SATRGB Background', value: InpaintOutputType.SATRGB_BACKGROUND },
              ].map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.name}
                </MenuItem>
              ))}
            </FormSelect>
            <FormTextInput type="number" name="dilation" label="Dilation" min={1} max={100} />
          </Stack>
          <Stack gap={4}>
            <Button
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting}
              type="submit"
              onClick={() => handleSubmit()}
            >
              Create
            </Button>
            <Button fullWidth variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Form>
  );
};
